import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Send } from "lucide-react";
import { api, adminHeaders, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }) + " ET"
    : "—";

export const WeeklyDigestCard = ({ onSent }) => {
  const [state, setState] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    const res = await api.get("/admin/price-digest", adminHeaders());
    setState(res.data);
  };
  useEffect(() => {
    load();
  }, []);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post("/admin/price-digest/send", {}, adminHeaders());
      const src = Object.entries(res.data.sources || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
      if (res.data.sent) toast.success(`Digest emailed — ${res.data.changes} price change${res.data.changes === 1 ? "" : "s"}. ${src}`);
      else toast.error("Price check ran but the email could not be sent.");
      await load();
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mb-5 rounded-lg border border-border bg-secondary/40 p-4" data-testid="weekly-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <CalendarClock size={20} className="mt-0.5 shrink-0 text-[#1B66C9]" />
          <div className="text-sm">
            <p className="font-bold">Weekly price-change email</p>
            <p className="text-muted-foreground" data-testid="digest-schedule">
              {state?.schedule || "Every Monday · 7:00 AM Eastern"} → {state?.recipient || "owner"}. Sent when a manufacturer
              price changed since the last check, or when an item is priced differently on fastduckmotors.com.
            </p>
            {state && (
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                <dt>Next run</dt>
                <dd data-testid="digest-next-run">{fmt(state.next_run)}</dd>
                <dt>Last check</dt>
                <dd data-testid="digest-last-run">
                  {fmt(state.last_run)}
                  {state.last_trigger ? ` (${state.last_trigger})` : ""}
                  {state.last_sources ? ` · ${Object.entries(state.last_sources).map(([k, v]) => `${k}: ${v}`).join(" · ")}` : ""}
                </dd>
                <dt>Last email</dt>
                <dd data-testid="digest-last-email">
                  {fmt(state.last_email_at)}
                  {state.last_email_changes != null ? ` · ${state.last_email_changes} change${state.last_email_changes === 1 ? "" : "s"}` : ""}
                </dd>
                <dt>Unsent changes</dt>
                <dd data-testid="digest-pending">{state.pending_changes}</dd>
                <dt>Website check</dt>
                <dd data-testid="digest-site-drift">
                  {state.site_checked_at
                    ? state.site_drift?.length
                      ? `${state.site_drift.length} item${state.site_drift.length === 1 ? "" : "s"} differ from fastduckmotors.com`
                      : "Builder matches fastduckmotors.com"
                    : "Not checked yet"}
                  {state.site_checked_at ? ` · ${fmt(state.site_checked_at)}` : ""}
                </dd>
              </dl>
            )}
            {state?.site_drift?.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs" data-testid="digest-site-drift-list">
                {state.site_drift.map((d) => (
                  <li key={d.product_id} data-testid={`site-drift-${d.product_id}`}>
                    <span className="font-semibold">{d.name}</span>: builder {money(d.price)} vs{" "}
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-[#1B66C9] underline">
                      website {money(d.site_price)}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <Button variant="outline" data-testid="digest-send-button" onClick={send} disabled={sending} className="font-bold">
          {sending ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Send size={16} className="mr-1.5" />}
          {sending ? "Checking manufacturers…" : "Send test digest now"}
        </Button>
      </div>
    </div>
  );
};
